import {useEffect, useRef, useState} from 'react';
import {getStorage, ref, getDownloadURL} from 'firebase/storage';
import {getAuth, onAuthStateChanged} from 'firebase/auth';

const ensureLoadingOverlay = () => {
    const existing =
        document.getElementById('firebase-loading-overlay');

    if (existing) {
        return existing;
    }

    /*
     * Fallback only.
     *
     * index.jsx normally creates this overlay first.
     * firebaseLoader reuses it rather than creating
     * a second loading screen.
     */
    const overlay = document.createElement('div');

    overlay.id = 'firebase-loading-overlay';
    overlay.textContent = 'Loading project...';

    Object.assign(overlay.style, {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#fff',
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem'
    });

    document.body.appendChild(overlay);

    return overlay;
};

const removeLoadingOverlay = () => {
    const existing =
        document.getElementById('firebase-loading-overlay');

    if (existing) {
        existing.remove();
    }
};

/*
 * Scratch's uploader creates a hidden file input.
 *
 * We wait for the newest matching input because other hidden
 * file inputs may exist elsewhere on the page.
 */
const waitForScratchInput = () =>
    new Promise(resolve => {
        let attempts = 0;

        const check = () => {
            const inputs =
                document.querySelectorAll(
                    'input[type="file"][style*="display: none"]'
                );

            if (inputs.length) {
                /*
                 * handleStartSelectingFileUpload creates a fresh input.
                 * The newest one should therefore be the last matching input.
                 */
                resolve(inputs[inputs.length - 1]);
                return;
            }

            attempts++;

            /*
             * Avoid polling forever if Scratch fails to create
             * the upload input for some unexpected reason.
             */
            if (attempts >= 100) {
                resolve(null);
                return;
            }

            setTimeout(check, 100);
        };

        check();
    });

const waitForFullscreenButton = () =>
    new Promise(resolve => {
        let attempts = 0;

        const check = () => {
            const button =
                document.querySelector(
                    '[title="Full Screen Control"]'
                );

            if (button) {
                resolve(button);
                return;
            }

            attempts++;

            if (attempts >= 50) {
                resolve(null);
                return;
            }

            setTimeout(check, 100);
        };

        check();
    });

export default function useFirebaseLoader (
    onStartSelectingFileUpload,
    onFileSelected
) {
    const [firebasePath, setFirebasePath] =
        useState(null);

    const [authReady, setAuthReady] =
        useState(false);

    /*
     * Tracks whether a Firebase load is currently running.
     */
    const loadingRef = useRef(false);

    /*
     * Last project successfully/attempted to be loaded.
     *
     * This prevents React rerenders from immediately loading
     * the same project again.
     */
    const loadedPathRef = useRef(null);

    /*
     * Last path seen by the sessionStorage watcher.
     *
     * This is separate from loadedPathRef because sessionStorage
     * may briefly retain a path while the loader is processing it.
     */
    const detectedPathRef = useRef(null);

    /*
     * If a new path appears while another project is still loading,
     * remember it.
     *
     * After the current load finishes, changing firebasePath to
     * this queued path allows the next project to load.
     */
    const queuedPathRef = useRef(null);

    /*
     * Watch continuously for firebaseProjectPath being posted
     * by Alyd Market.
     *
     * IMPORTANT:
     * The old version stopped polling after finding the first path.
     * That could leave the old Scratch project in the VM when
     * another project was opened later.
     */
    useEffect(() => {
        let cancelled = false;
        let timer = null;

        const checkPath = () => {
            if (cancelled) {
                return;
            }

            if (
                sessionStorage.getItem('justLoggedOut') ===
                'true'
            ) {
                console.log(
                    '[AUTH] Skipping loader — user just logged out'
                );

                /*
                 * Keep polling in case logout state is later cleared.
                 */
                timer = setTimeout(checkPath, 200);
                return;
            }

            const rawPath =
                sessionStorage.getItem(
                    'firebaseProjectPath'
                );

            if (rawPath) {
                const decoded =
                    decodeURIComponent(rawPath);

                /*
                 * Only react when the posted path has changed.
                 */
                if (
                    detectedPathRef.current !== decoded
                ) {
                    detectedPathRef.current = decoded;

                    console.log(
                        '[HOOK] 🚀 New firebaseProjectPath detected:',
                        decoded
                    );

                    if (loadingRef.current) {
                        /*
                         * A project is already being loaded.
                         *
                         * Do not interrupt the current FileReader /
                         * Scratch VM load halfway through.
                         * Queue the latest requested project instead.
                         */
                        console.log(
                            '[HOOK] ⏳ Project load already running. Queuing:',
                            decoded
                        );

                        queuedPathRef.current = decoded;
                    } else {
                        setFirebasePath(decoded);
                    }
                }
            } else {
                /*
                 * firebaseLoader removes the path after accepting it.
                 *
                 * Resetting this allows that same path to be detected
                 * again later if the user intentionally opens the same
                 * project again.
                 */
                detectedPathRef.current = null;
            }

            /*
             * KEEP WATCHING.
             *
             * This is the critical difference from the old loader.
             */
            timer = setTimeout(checkPath, 200);
        };

        checkPath();

        return () => {
            cancelled = true;

            if (timer) {
                clearTimeout(timer);
            }
        };
    }, []);

    /*
     * Wait for Firebase authentication.
     */
    useEffect(() => {
        const auth = getAuth();

        const unsubscribe =
            onAuthStateChanged(auth, user => {
                if (user) {
                    console.log(
                        '[HOOK] ✅ Auth ready for Firebase loader'
                    );

                    setAuthReady(true);
                } else {
                    setAuthReady(false);
                }
            });

        return () => unsubscribe();
    }, []);

    /*
     * Download and inject the Firebase SB3 once both:
     *
     * 1. the selected project path exists
     * 2. Firebase authentication is ready
     */
    useEffect(() => {
        if (!firebasePath || !authReady) {
            return;
        }

        if (
            !onStartSelectingFileUpload ||
            !onFileSelected
        ) {
            console.warn(
                '[HOOK] ❌ Missing dependencies to load'
            );

            return;
        }

        /*
         * Don't load the same path twice from React rerenders.
         */
        if (
            loadingRef.current ||
            loadedPathRef.current === firebasePath
        ) {
            return;
        }

        let cancelled = false;

        const loadFromFirebase = async () => {
            loadingRef.current = true;
            loadedPathRef.current = firebasePath;

            console.log(
                '[HOOK] ✅ Firebase loader triggered with path:',
                firebasePath
            );

            ensureLoadingOverlay();

            try {
                /*
                 * Create Scratch's hidden file input.
                 *
                 * This still uses the existing uploader architecture
                 * so older loading behavior is retained.
                 */
                onStartSelectingFileUpload();

                const storage = getStorage();

                const fileRef =
                    ref(storage, firebasePath);

                const url =
                    await getDownloadURL(fileRef);

                if (cancelled) {
                    return;
                }

                const response =
                    await fetch(url);

                if (!response.ok) {
                    throw new Error(
                        `Firebase project download failed: ${
                            response.status
                        }`
                    );
                }

                const blob =
                    await response.blob();

                if (cancelled) {
                    return;
                }

                const fileName =
                    decodeURIComponent(
                        firebasePath
                            .split('/')
                            .pop() ||
                        'project.sb3'
                    );

                const file =
                    new File(
                        [blob],
                        fileName,
                        {
                            type:
                                'application/x.scratch.sb3'
                        }
                    );

                /*
                 * Wait for Scratch's freshly-created file input.
                 */
                const input =
                    await waitForScratchInput();

                if (cancelled) {
                    return;
                }

                if (!input) {
                    throw new Error(
                        'Scratch upload input was not created.'
                    );
                }

                /*
                 * Mark this upload as automatic/Firebase-driven.
                 *
                 * sb-file-uploader-hoc uses this to bypass the
                 * normal local-file upload confirmation/state flow.
                 */
                input.dataset.source =
                    'firebase-loader';

                const dataTransfer =
                    new DataTransfer();

                dataTransfer.items.add(file);

                input.files =
                    dataTransfer.files;

                /*
                 * Dispatch exactly one change event.
                 *
                 * The uploader's existing onchange handler takes over
                 * and ultimately calls vm.loadProject().
                 */
                const changeEvent =
                    new Event(
                        'change',
                        {
                            bubbles: true
                        }
                    );

                input.dispatchEvent(
                    changeEvent
                );

                console.log(
                    '[HOOK] ✅ File injected into editor:',
                    firebasePath
                );

                /*
                 * At this point sb-file-uploader-hoc owns:
                 *
                 * vm.loadProject()
                 * target creation
                 * sprites
                 * backdrops
                 * blocks
                 */
                const fullscreenButton =
                    await waitForFullscreenButton();

                if (cancelled) {
                    return;
                }

                /*
                 * Preserve your existing settling delay before
                 * entering fullscreen/viewer mode.
                 */
                setTimeout(() => {
                    if (cancelled) {
                        return;
                    }

                    if (fullscreenButton) {
                        console.log(
                            '[HOOK] 🔘 Triggering fullscreen'
                        );

                        fullscreenButton.click();
                    } else {
                        console.warn(
                            '[HOOK] ⚠️ Fullscreen button not found'
                        );
                    }

                    /*
                     * Allow React/Scratch to paint the final project
                     * before exposing the viewer.
                     */
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            setTimeout(() => {
                                if (!cancelled) {
                                    console.log(
                                        '[HOOK] ✅ Viewer ready — removing loading overlay'
                                    );

                                    removeLoadingOverlay();
                                }
                            }, 300);
                        });
                    });
                }, 1500);

                /*
                 * This path has now been accepted by the loader.
                 *
                 * Remove it from sessionStorage so future selections
                 * can be detected cleanly.
                 */
                const storedPath =
                    sessionStorage.getItem(
                        'firebaseProjectPath'
                    );

                if (
                    storedPath &&
                    decodeURIComponent(storedPath) ===
                    firebasePath
                ) {
                    sessionStorage.removeItem(
                        'firebaseProjectPath'
                    );
                }

            } catch (err) {
                console.error(
                    '[HOOK] ❌ Firebase load error:',
                    err
                );

                /*
                 * Allow this path to be retried after an error.
                 */
                loadedPathRef.current = null;

                removeLoadingOverlay();
            } finally {
                loadingRef.current = false;

                /*
                 * If another project was selected while this one
                 * was loading, immediately hand off to the newest
                 * requested path.
                 */
                const queuedPath =
                    queuedPathRef.current;

                queuedPathRef.current = null;

                if (
                    !cancelled &&
                    queuedPath &&
                    queuedPath !== firebasePath
                ) {
                    console.log(
                        '[HOOK] ▶️ Loading queued Firebase project:',
                        queuedPath
                    );

                    setFirebasePath(
                        queuedPath
                    );
                }
            }
        };

        loadFromFirebase();

        return () => {
            cancelled = true;
        };
    }, [
        firebasePath,
        authReady,
        onStartSelectingFileUpload,
        onFileSelected
    ]);
}